import PaymentSettings from "./products/schedulingBookings/pages/customer/booking/paymentSettings";
import WorkspacePortal from "./platform/pages/business/portal";
import PaymentResult from "./products/schedulingBookings/pages/customer/booking/paymentResult";
import useLoadReduxStoreData from "./platform/redux/reducers/loadReduxStoreData";
import WorkspacesPage from "./apps/timely/WorkspacesPage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ConnectPlanyPage from "./apps/timely/ConnectPlanyPage";
import { Navigate, Route, Routes, useParams, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { isConciergeUser } from "./platform/util/conciergeAccess";
import { GET_BOOKING_TYPES, GET_TOKEN, BusinessQueryNames } from "./platform/graphql/queries";
import BusinessPortal from "./platform/pages/business/businessPage/businessPortal";
import CategoryPage from "./platform/pages/customer/categoryPage";
import PricingPage from "./platform/pages/business/pricingPage.jsx";
import ForgotPassword from "./platform/components/auth/forgotPassword";
import { CalendarProvider } from "./products/schedulingBookings/components/business/calendar/calendarContext";
import Login from "./platform/components/auth/login/login";
import SignUp from "./platform/components/auth/signup";
import UserProfilePage from "./platform/pages/userPortal/userPortal";
import UpdateStaffStatus from "./products/staffCaregiverHiring/components/business/staff/updateStaffStatus";
import IntegrationHeader from "./platform/components/integrations/integrationHeader";
import Integration from "./platform/components/integrations/integration";
import IntegrationPage from "./platform/components/integrations/integrationPage";
import CalendarIntegration from "./platform/components/business/integeration/calendarIntegration";
import { Calendar, PrintPortal, EmbeddedBooking, BookingTypePage, UserBookingTypePage, BookingPage, ConfirmPage, ScheduledBookingType, CreateNewDashBoard, BookingTypeEntryForm, BookingTypeHomePortal } from "./products/schedulingBookings";
import BusinessBookingTypes from "./products/schedulingBookings/components/business/bookingType/businessBookingTypes";
import BookingDashboardProductPage from "./products/booking/pages/BookingDashboardProductPage";
import BookingPublicLandingPage from "./products/booking/pages/BookingPublicLandingPage";
import BookingStartPublicPage from "./products/booking/pages/BookingStartPublicPage";
import BookingCategoryPage from "./products/booking/pages/BookingCategoryPage";
import ConnectAgentCraftyPage from "./platform/pages/customer/ConnectAgentCraftyPage";
import ConnectTelegramPage from "./platform/pages/customer/ConnectTelegramPage";
import AllowAccessPage from "./platform/pages/customer/AllowAccessPage";
import TimelyInvitePage from "./platform/pages/customer/TimelyInvitePage";
import TimelyCaptureInbox from "./platform/pages/business/TimelyCaptureInbox";
import StaffPortal from "./products/staffCaregiverHiring/pages/business/staffPage/staffPortal";
import Error from "./platform/components/common/error";
import Main from "./platform/components/common/main";
import TimelyLegalPage from "./platform/pages/privacy/timelyLegalPage";
import SubscriptionSuccessPage from "./platform/pages/business/subscriptionSuccessPage";
import ValidateBusinessUrl from "./platform/util/validateBusinessId";
import RootRedirect from "./platform/routes/rootRedirect";
import UserPage from "./platform/pages/business/userPage/userPage";
import BillingSuccess from "./platform/components/common/strip/billingSucess";
import BillingCancel from "./platform/components/common/strip/billingCancel";
import { isPathProductEnabled } from "./products/config/productFlags";
import { setSelectedBusiness } from "./platform/redux/actions/businessActions";
import { useSelectBusinesses, useSelectedBusiness } from "./platform/redux/store";
import { UPDATE_DEFAULT_BUSINESS } from "./platform/graphql/mutations";
import { setToken as persistToken, decodeSessionToken, getSafeReturnTo, getLoginDestination } from "./platform/util/authUtils";
import { hasStandaloneBookingAccess, isProductSubscribed, isProductVisible } from "./core/business/productAccess";
import { isReservedBusinessSlug } from "./platform/util/businessSlug";
import { getRuntimeAppProfile, getRuntimeBusinessHomePath, getCrossAppUrl, getRuntimePublicHomePath } from "./platform/runtime/appProfile";
const LOCAL_WORKSPACE_PRODUCTS_KEY = "planyLocalWorkspaceProducts";
function readLocalWorkspaceProducts(businessId) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_WORKSPACE_PRODUCTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const guestProducts = parsed.guest || {};
    const businessProducts = businessId ? parsed[String(businessId)] || {} : {};
    return {
      ...guestProducts,
      ...businessProducts
    };
  } catch {
    return {};
  }
}
function hasLocalWorkspaceProduct(providerUser, productId) {
  const businessId = Number(providerUser?.defaultBusinessId || 0) || null;
  const localWorkspaceProducts = readLocalWorkspaceProducts(businessId);
  return Boolean(localWorkspaceProducts?.[productId]);
}
function hasLocalWorkspaceOverride(providerUser, productId) {
  const businessId = Number(providerUser?.defaultBusinessId || 0) || null;
  const localWorkspaceProducts = readLocalWorkspaceProducts(businessId);
  return Object.prototype.hasOwnProperty.call(localWorkspaceProducts, productId);
}
function hasWorkspaceProductAccess(providerUser, productId) {
  if (hasLocalWorkspaceOverride(providerUser, productId)) {
    return hasLocalWorkspaceProduct(providerUser, productId);
  }
  return isProductSubscribed(providerUser, productId) || isProductVisible(providerUser, productId);
}
function RuntimeAppBoundary({
  children
}) {
  const location = useLocation();
  const targetUrl = getCrossAppUrl(location.pathname, location.search);
  useEffect(() => {
    if (targetUrl) window.location.replace(targetUrl);
  }, [targetUrl]);
  if (targetUrl) {
    return <Typography sx={{
      p: 2
    }}>Opening the correct application...</Typography>;
  }
  return children;
}
const RouteGroups = ({
  providerUser,
  setProviderUser,
  token,
  login,
  logout,
  setImage
}) => {
  const runtimeAppProfile = getRuntimeAppProfile();
  const dispatch = useDispatch();
  const location = useLocation();
  const [myBookingTypes, setMyBookingTypes] = useState(null);
  const assigningDefaultBusinessRef = useRef(false);
  const selectedBusinessForStaff = useSelectedBusiness();
  const businesses = useSelectBusinesses();
  const shouldBootstrapSessionData = useMemo(() => {
    const pathname = location.pathname;
    return pathname === "/biz" || pathname.startsWith("/biz/") || pathname === "/profile" || pathname === "/workspaces" || pathname === "/myschedule";
  }, [location.pathname]);
  const { loading: workspacesLoading, error: workspacesError, data: workspacesData } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: shouldBootstrapSessionData }
  );
  // Query results arrive one render before the loader copies them into Redux.
  // Do not send an existing workspace member to setup during that render.
  const hasAnyBusiness = useMemo(() =>
    (Array.isArray(businesses) && businesses.some(b => Number(b?.businessBasic?.id) > 0)) ||
    Boolean(workspacesData?.myBusinesses?.some(b => Number(b?.id) > 0)),
  [businesses, workspacesData]);
  const effectiveStaffId = useMemo(() => {
    const rawStaffId = Number(providerUser?.staffId);
    if (Number.isFinite(rawStaffId) && rawStaffId > 0) return rawStaffId;
    const businessStaffs = selectedBusinessForStaff?.businessStaffs || [];
    const providerUserId = Number(providerUser?.userId);
    if (!Number.isFinite(providerUserId) || providerUserId <= 0) return null;
    const matchedStaff = businessStaffs.find(staff => Number(staff?.user?.id) === providerUserId);
    const matchedStaffId = Number(matchedStaff?.id);
    return Number.isFinite(matchedStaffId) && matchedStaffId > 0 ? matchedStaffId : null;
  }, [providerUser?.staffId, providerUser?.userId, selectedBusinessForStaff?.businessStaffs]);
  const {
    loading: myBookingTypesLoading,
    error: myBookingTypesError,
    data: myBookingTypesData,
    refetch: myBookingTypesDataRefetch
  } = useQuery(GET_BOOKING_TYPES, {
    variables: {
      staffId: Number(effectiveStaffId)
    },
    skip: !shouldBootstrapSessionData || !effectiveStaffId
  });
  const {
    loading: providerUserLoading,
    error: providerUserError,
    data: providerUserData,
    refetch: refetchProviderToken
  } = useQuery(GET_TOKEN, {
    variables: {
      userId: providerUser?.userId ? Number(providerUser.userId) : null,
      businessId: providerUser?.defaultBusinessId ? Number(providerUser.defaultBusinessId) : null,
      staffId: null,
      subscriptionId: null
    },
    skip: !shouldBootstrapSessionData || !providerUser?.userId
  });
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);
  useEffect(() => {
    if (!myBookingTypesLoading && !myBookingTypesError && myBookingTypesData) {
      setMyBookingTypes(myBookingTypesData?.getBookingTypes);
    }
  }, [myBookingTypesLoading, myBookingTypesError, myBookingTypesData]);
  useEffect(() => {
    if (!providerUserLoading && providerUserError?.graphQLErrors?.some(error => error.message === "Invalid Token")) {
      logout();
      return;
    }
    if (!providerUserLoading && !providerUserError && providerUserData) {
      const nextToken = providerUserData?.getToken?.token;
      const decoded = decodeSessionToken(nextToken);
      if (!decoded) {
        logout();
        return;
      }
      persistToken(nextToken);
      setProviderUser(decoded);
    }
  }, [providerUserLoading, providerUserError, providerUserData, setProviderUser, logout]);
  useEffect(() => {
    const hasUser = Number(providerUser?.userId || 0) > 0;
    const hasDefault = Number(providerUser?.defaultBusinessId || 0) > 0;
    const isAgentUser = isConciergeUser(providerUser);
    const fallbackId = Number(selectedBusinessForStaff?.businessBasic?.id || businesses?.[0]?.businessBasic?.id || 0);
    if (!hasUser || hasDefault || isAgentUser || fallbackId <= 0 || assigningDefaultBusinessRef.current) {
      return;
    }
    assigningDefaultBusinessRef.current = true;
    (async () => {
      try {
        dispatch(setSelectedBusiness(fallbackId));
        await updateDefaultBusiness({
          variables: {
            defaultBusinessId: fallbackId
          }
        });
        const tokenResult = await refetchProviderToken({
          userId: Number(providerUser.userId),
          businessId: fallbackId,
          staffId: null,
          subscriptionId: null
        });
        const nextToken = tokenResult?.data?.getToken?.token;
        const decoded = decodeSessionToken(nextToken);
        if (decoded) {
          persistToken(nextToken);
          setProviderUser(decoded);
        } else {
          logout();
        }
      } catch (error) {
        console.error("Failed to auto-assign default business:", error);
      } finally {
        assigningDefaultBusinessRef.current = false;
      }
    })();
  }, [providerUser?.userId, providerUser?.defaultBusinessId, selectedBusinessForStaff?.businessBasic?.id, businesses, dispatch, updateDefaultBusiness, refetchProviderToken, setProviderUser, logout]);
  useEffect(() => {
    const hasSelectedBusiness = Number(selectedBusinessForStaff?.businessBasic?.id || 0) > 0;
    if (hasSelectedBusiness || !hasAnyBusiness) return;
    const fallbackId = Number(businesses?.[0]?.businessBasic?.id || 0);
    if (fallbackId > 0) {
      dispatch(setSelectedBusiness(fallbackId));
    }
  }, [dispatch, selectedBusinessForStaff?.businessBasic?.id, hasAnyBusiness, businesses]);
  useEffect(() => {
    const defaultBusinessId = Number(providerUser?.defaultBusinessId || 0);
    const selectedBusinessId = Number(selectedBusinessForStaff?.businessBasic?.id || 0);
    if (!shouldBootstrapSessionData || defaultBusinessId <= 0) return;
    if (selectedBusinessId === defaultBusinessId) return;
    const hasDefaultBusinessLoaded = businesses.some(business => Number(business?.businessBasic?.id || 0) === defaultBusinessId);
    if (hasDefaultBusinessLoaded) {
      dispatch(setSelectedBusiness(defaultBusinessId));
    }
  }, [providerUser?.defaultBusinessId, selectedBusinessForStaff?.businessBasic?.id, shouldBootstrapSessionData, businesses, dispatch]);
  const memoizedBookingTypes = useMemo(() => myBookingTypes, [myBookingTypes]);
  const hasLoading = useMemo(() => workspacesLoading || myBookingTypesLoading || providerUserLoading, [workspacesLoading, myBookingTypesLoading, providerUserLoading]);
  const hasError = useMemo(() => workspacesError || myBookingTypesError || providerUserError, [workspacesError, myBookingTypesError, providerUserError]);
  const isCurrentPathEnabled = useMemo(() => isPathProductEnabled(location.pathname), [location.pathname]);
  const guestBizEntryRoute = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const productKey = params.get("v");
    if (productKey === "booking") return "/booking";
    return runtimeAppProfile.publicHomePath;
  }, [location.search, runtimeAppProfile.publicHomePath]);
  const shouldGateRouteLoading = shouldBootstrapSessionData;
  const ProtectedComponent = ({
    children
  }) => {
    const location = useLocation();
    const isAgentUser = isConciergeUser(providerUser);
    if (!providerUser.userId) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    if (!providerUser?.defaultBusinessId && !hasAnyBusiness && !isAgentUser && location.pathname !== "/biz/business") {
      if (!localStorage.getItem("redirectAfterBusiness")) {
        const loginRedirect = getSafeReturnTo(localStorage.getItem("redirectAfterLogin"));
        const intended = loginRedirect || location.pathname + location.search;
        localStorage.setItem("redirectAfterBusiness", intended);
      }
      return <Navigate to="/workspaces" replace />;
    }
    return <>{children}</>;
  };
  const ProtectedComponentNeedToHaveBusiness = ({
    children
  }) => {
    if (!token) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    if (!providerUser?.defaultBusinessId && !hasAnyBusiness && location.pathname !== "/biz/business") {
      return <Navigate to="/workspaces" replace />;
    }
    return children;
  };
  const RequireLoginOnly = ({
    children
  }) => {
    const isAuthed = Boolean(token || providerUser?.userId);
    if (!isAuthed) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
  };
  const RequireStandaloneBookingAccess = ({
    children
  }) => {
    if (!providerUser?.userId) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    const canUseBookingPortal = hasStandaloneBookingAccess(providerUser) || hasWorkspaceProductAccess(providerUser, "booking");
    if (!canUseBookingPortal && providerUser?.defaultBusinessId) {
      return <Navigate to="/biz/pricing?product=booking" state={{ from: "/workspaces" }} replace />;
    }
    return <>{children}</>;
  };
  const StaffGoogleCallbackRedirect = ({
    children
  }) => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const hasPendingAuth = Boolean(localStorage.getItem("calendarAuthPending"));
    if (code && hasPendingAuth) {
      return <Navigate to={`/connect/google_calendar${location.search}`} replace />;
    }
    return children;
  };
  const NotFound = () => <Error />;
  const renderError = () => <Typography variant="h6" color="error" align="center">
      An error occurred while loading the data. Please try again.
    </Typography>;
  const renderLoading = () => <div style={{
    textAlign: "center",
    padding: "20px"
  }}>
      <h2>Loading...</h2>
      <p>Please wait while we fetch the data.</p>
      <div className="spinner" />
    </div>;
  const postLoginRedirect = useMemo(() => getLoginDestination({ ...providerUser, defaultBusinessId: providerUser?.defaultBusinessId || (hasAnyBusiness ? 1 : null) }, localStorage.getItem("redirectAfterLogin")), [hasAnyBusiness, providerUser]);
  function PublicWorkspaceRoute({ children }) {
    const { businessUrl } = useParams();
    if (!businessUrl || isReservedBusinessSlug(businessUrl)) return <NotFound />;
    return children;
  }
  function LegacyPathRedirect({
    build
  }) {
    const params = useParams();
    const loc = useLocation();
    const target = build(Object.fromEntries(Object.entries(params).map(([key, value]) => [key, encodeURIComponent(value || "")])));
    return <Navigate to={`${target}${loc.search}${loc.hash}`} replace />;
  }
  const routes = useMemo(() => <Routes>
        {}
        <Route path="/" element={<RootRedirect providerUser={providerUser} />} />

        {}
        <Route path="/choose" element={<Navigate to={getRuntimePublicHomePath()} replace />} />

        {}

        <Route path="/booking" element={<BookingPublicLandingPage />} />
        <Route path="/booking/start" element={<BookingStartPublicPage />} />
        <Route path="/booking/category/:category" element={<BookingCategoryPage />} />
        <Route path="/connect/agentcrafty" element={<ConnectAgentCraftyPage />} />
        <Route path="/b/connect/agentcrafty" element={<ConnectAgentCraftyPage />} />
        <Route path="/connect/telegram" element={<ConnectTelegramPage />} />
        <Route path="/allow-access" element={<AllowAccessPage />} />
        <Route path="/i/:token" element={<TimelyInvitePage />} />
        <Route path="/booking/dashboard" element={<ProtectedComponent>
              <RequireStandaloneBookingAccess>
                <BookingDashboardProductPage />
              </RequireStandaloneBookingAccess>
            </ProtectedComponent>} />

        {}

        {}
        {}

        {}

        {}
        <Route path="/biz/dashboard" element={<Navigate to="/biz/bookingtypes?v=booking" replace />} />

        {}
        {}
        <Route path="/:businessUrl" element={<PublicWorkspaceRoute><BusinessBookingTypes /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/:bookingTypeLink" element={<PublicWorkspaceRoute><BookingTypePage /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/team/:staffUrl" element={<PublicWorkspaceRoute><ValidateBusinessUrl><UserBookingTypePage /></ValidateBusinessUrl></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/book/:staffUrl/:bookingTypeLink/:time" element={<PublicWorkspaceRoute><BookingPage /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/confirmed/:title/:timeRange/:time/:color/:staffUrl/:userName" element={<PublicWorkspaceRoute><ConfirmPage /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/team/:staffUrl/wallet" element={<PublicWorkspaceRoute><Navigate to="/biz/payments" replace /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/team/:staffUrl/tip" element={<PublicWorkspaceRoute><Navigate to="/biz/payments" replace /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/tip" element={<PublicWorkspaceRoute><Navigate to="/biz/payments" replace /></PublicWorkspaceRoute>} />
        <Route path="/b/wallet" element={<Navigate to="/biz/payments" replace />} />
        <Route path="/booking/payment/:id" element={<PaymentResult />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancel" element={<BillingCancel />} />
        <Route path="/book/embed/:businessUrl" element={<CalendarProvider><EmbeddedBooking embedded /></CalendarProvider>} />
        <Route path="/embed/:businessUrl" element={<LegacyPathRedirect build={({businessUrl}) => `/book/embed/${businessUrl}`} />} />
        <Route path="/embed/:businessUrl/booking/:bookingTypeLink" element={<BookingTypePage />} />
        <Route path="/b/:businessUrl" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl}) => `/${businessUrl}`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/booking" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl}) => `/${businessUrl}`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/booking/:bookingTypeLink" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, bookingTypeLink}) => `/${businessUrl}/${bookingTypeLink}`} /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/booking" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl}) => `/${businessUrl}`} /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/booking/:bookingTypeLink" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, bookingTypeLink}) => `/${businessUrl}/${bookingTypeLink}`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/:staffUrl" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, staffUrl}) => `/${businessUrl}/team/${staffUrl}`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/:staffUrl/:bookingTypeLink/:time" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, staffUrl, bookingTypeLink, time}) => `/${businessUrl}/book/${staffUrl}/${bookingTypeLink}/${time}`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/booking/servicesConfirmed/:title/:timeRange/:time/:color/:staffUrl/:userName" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, title, timeRange, time, color, staffUrl, userName}) => `/${businessUrl}/confirmed/${title}/${timeRange}/${time}/${color}/${staffUrl}/${userName}`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/:staffUrl/wallet" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, staffUrl}) => `/${businessUrl}/team/${staffUrl}/wallet`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/:staffUrl/tip" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, staffUrl}) => `/${businessUrl}/team/${staffUrl}/tip`} /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/:staffUrl/:bookingTypeLink/:time" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, staffUrl, bookingTypeLink, time}) => `/${businessUrl}/book/${staffUrl}/${bookingTypeLink}/${time}`} /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/booking/servicesConfirmed/:title/:timeRange/:time/:color/:staffUrl/:userName" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, title, timeRange, time, color, staffUrl, userName}) => `/${businessUrl}/confirmed/${title}/${timeRange}/${time}/${color}/${staffUrl}/${userName}`} /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/:staffUrl/wallet" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, staffUrl}) => `/${businessUrl}/team/${staffUrl}/wallet`} /></PublicWorkspaceRoute>} />
        <Route path="/:businessUrl/:staffUrl/tip" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl, staffUrl}) => `/${businessUrl}/team/${staffUrl}/tip`} /></PublicWorkspaceRoute>} />
        <Route path="/b/:businessUrl/tip" element={<PublicWorkspaceRoute><LegacyPathRedirect build={({businessUrl}) => `/${businessUrl}/tip`} /></PublicWorkspaceRoute>} />

        <Route path="/biz" element={token ? <Navigate to={hasAnyBusiness ? postLoginRedirect : "/workspaces"} replace /> : <Navigate to={guestBizEntryRoute} replace />} />
        {}

        <Route path="/biz/products" element={<ProtectedComponent>
              <Navigate to={getRuntimeBusinessHomePath()} replace />
            </ProtectedComponent>} />
        <Route path="/biz/payments" element={<ProtectedComponent><WorkspacePortal TabComponent={PaymentSettings} title="Appointment payments" props={{}} /></ProtectedComponent>} />
        <Route path="/biz/pricing" element={<PricingPage />} />

        <Route path="/login" element={!token ? <Login token={token} login={login} /> : <Navigate to={postLoginRedirect} replace />} />
        <Route path="/start-free-trial" element={!token ? <SignUp token={token} login={login} /> : <Navigate to={postLoginRedirect} replace />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/biz/business/:tabId?" element={<ProtectedComponent>

                {hasAnyBusiness && selectedBusinessForStaff?.businessBasic?.workspaceType !== "PERSONAL" ? <BusinessPortal /> : <WorkspacesPage />}

            </ProtectedComponent>} />
        <Route path="/biz/calendar" element={<ProtectedComponent>
              <RequireStandaloneBookingAccess>
                <Calendar myBookingTypes={memoizedBookingTypes} />
              </RequireStandaloneBookingAccess>
            </ProtectedComponent>} />
        <Route path="/biz/capture" element={<ProtectedComponent>
              <TimelyCaptureInbox />
            </ProtectedComponent>} />
        <Route path="/biz/printview" element={<ProtectedComponent>
              <PrintPortal myBookingTypes={memoizedBookingTypes} />
            </ProtectedComponent>} />
        <Route path="/biz/approve/:token" element={<UpdateStaffStatus />} />
        <Route path="/signup" element={!token ? <SignUp login={login} /> : <Navigate to={postLoginRedirect} replace />} />
        <Route path="/biz/integrations/:integration" element={<ProtectedComponent>
              <Main content={IntegrationHeader} display={Integration} data={memoizedBookingTypes} />
            </ProtectedComponent>} />
        <Route path="/biz/bookingtypes" element={<ProtectedComponent>
              <RequireStandaloneBookingAccess>
                <Main content={BookingTypeHomePortal} data={memoizedBookingTypes} refetch={myBookingTypesDataRefetch} />
              </RequireStandaloneBookingAccess>
            </ProtectedComponent>} />
        <Route path="/biz/create/:bookingtypecategory" element={<ProtectedComponent>
              <RequireStandaloneBookingAccess>
                <Main content={CreateNewDashBoard} display={BookingTypeEntryForm} data={memoizedBookingTypes} />
              </RequireStandaloneBookingAccess>
            </ProtectedComponent>} />
        <Route path="/biz/update/:id" element={<ProtectedComponent>
              <RequireStandaloneBookingAccess>
                <Main content={CreateNewDashBoard} display={BookingTypeEntryForm} data={memoizedBookingTypes} setImage={setImage} />
              </RequireStandaloneBookingAccess>
            </ProtectedComponent>} />
        <Route path="/biz/staff" element={<ProtectedComponent>
              <StaffGoogleCallbackRedirect>
                <StaffPortal />
              </StaffGoogleCallbackRedirect>
            </ProtectedComponent>} />

        <Route path="/biz/integrations" element={<ProtectedComponent>
              <Main content={IntegrationHeader} display={IntegrationPage} data={memoizedBookingTypes} />
            </ProtectedComponent>} />
        <Route path="/workspaces" element={<RequireLoginOnly><WorkspacesPage /></RequireLoginOnly>} />
        <Route path="/connect/plany" element={<RequireLoginOnly><ConnectPlanyPage /></RequireLoginOnly>} />
        <Route path="/connect/google_calendar" element={<ProtectedComponent>
              <CalendarIntegration />
            </ProtectedComponent>} />

        {}

        <Route path="/category/:category" element={<CategoryPage />} />
        <Route path="/about" element={<TimelyLegalPage />} />
        <Route path="/privacy" element={<TimelyLegalPage />} />
        <Route path="/terms" element={<TimelyLegalPage />} />
        <Route path="/sms-opt-in" element={<TimelyLegalPage />} />
        <Route path="/security" element={<TimelyLegalPage />} />
        <Route path="/contact" element={<TimelyLegalPage />} />

        <Route path="/faq" element={<TimelyLegalPage />} />

        {}
        <Route path="/coins/buy" element={<Navigate to="/biz/payments" replace />} />
        <Route path="/myschedule" element={<RequireLoginOnly>
                <CalendarProvider>
                  <ScheduledBookingType />
                </CalendarProvider>
            </RequireLoginOnly>} />

        <Route path="/profile" element={<UserPage logout={logout} token={token} />} />
        <Route path="/biz/profile" element={<ProtectedComponent>
              <UserProfilePage logout={logout} token={token} />
            </ProtectedComponent>} />
        <Route path="/biz/subscription-success/:planName" element={<ProtectedComponent>
              <SubscriptionSuccessPage />
            </ProtectedComponent>} />

        {}
        <Route path="*" element={<NotFound />} />
      </Routes>, [guestBizEntryRoute, hasAnyBusiness, location.hash, location.pathname, location.search, login, logout, memoizedBookingTypes, myBookingTypesDataRefetch, postLoginRedirect, providerUser, selectedBusinessForStaff?.businessBasic?.workspaceType, setImage, token]);
  const BusinessRoutes = useMemo(() => {
    if (!isCurrentPathEnabled) return <NotFound />;
    if (shouldGateRouteLoading && hasLoading) return <>{renderLoading()}</>;
    if (shouldGateRouteLoading && hasError) return <>{renderError()}</>;
    return <>{routes}</>;
  }, [hasLoading, hasError, isCurrentPathEnabled, routes, shouldGateRouteLoading]);
  return <RuntimeAppBoundary>{BusinessRoutes}</RuntimeAppBoundary>;
};
export default RouteGroups;
